import React from 'react';
import { SkeletonLines } from '@/components/Admin/UI/Skeleton';
import styled from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import { colors } from '@/constants/colors';
import { getIspInvoices, type IspInvoiceRow } from '@/api/isp';

/**
 * Every bill this customer has ever had, paid or not.
 *
 * Opened from their row on Accounts. An operator on the phone is asked "how much
 * do I owe?" and needs the answer plus the working — a single balance figure
 * invites an argument no one can settle, a list of bills ends it.
 */
interface Props {
   customerId: string;
   customerName: string;
   onCancel: () => void;
   /**
    * Record a payment against one bill, from here.
    *
    * Ray, 2026-09-01: *"put also pay now action button so operator can manually
    * set this payment as alternative when customer doesnt pay in the portl."*
    * Most subscribers will hand over cash at the office or send a GCash the
    * operator sees in their own app — the portal is the exception, not the
    * rule, and this list was read-only.
    */
   onPay?: (invoice: IspInvoiceRow) => void;
}

const peso = (n: number) => `₱${Number(n ?? 0).toLocaleString()}`;
const day = (d: string) => new Date(d).toISOString().slice(0, 10);
const isOverdue = (i: IspInvoiceRow) =>
   i.outstanding > 0 && new Date(i.dueAt).getTime() < Date.now();

const IspAccountInvoices: React.FC<Props> = ({
   onPay,
   customerId,
   customerName,
   onCancel,
}) => {
   const { data: invoices = [], isLoading } = useQuery({
      queryKey: ['isp-invoices', customerId],
      queryFn: () => getIspInvoices({ customer: customerId }),
   });

   const owed = invoices.reduce((s, i) => s + i.outstanding, 0);
   const paid = invoices.reduce((s, i) => s + i.paidAmount, 0);

   return (
      <Wrapper>
         <Head>
            <div>
               <Who>{customerName}</Who>
               <Sub>
                  {invoices.length} bill{invoices.length === 1 ? '' : 's'} · {peso(paid)}{' '}
                  paid
               </Sub>
            </div>
            <Owed $on={owed > 0}>
               {owed > 0 ? `${peso(owed)} outstanding` : 'Nothing owed'}
            </Owed>
         </Head>

         {isLoading && <SkeletonLines lines={3} />}
         {!isLoading && invoices.length === 0 && (
            <Empty>No bills yet. They appear on this customer&apos;s due day.</Empty>
         )}

         {invoices.length > 0 && (
            <List>
               {invoices.map((i) => (
                  <Row key={i.id}>
                     <Left>
                        <Num>{i.number}</Num>
                        <Meta>
                           {i.lines.map((l) => l.planName).join(', ') || '—'} · due{' '}
                           {day(i.dueAt)}
                           {isOverdue(i) && <Late>overdue</Late>}
                        </Meta>
                     </Left>
                     <Right>
                        <Total>{peso(i.total)}</Total>
                        {/* The working, not just the verdict: an operator on the
                            phone needs to say "you paid 500 of 999". */}
                        <Paid $part={i.outstanding > 0 && i.paidAmount > 0}>
                           {i.outstanding > 0
                              ? i.paidAmount > 0
                                 ? `${peso(i.paidAmount)} paid · ${peso(i.outstanding)} left`
                                 : 'unpaid'
                              : 'paid'}
                        </Paid>
                        {/* Only where there is something to pay. On a settled
                            bill it would be a button that takes money against
                            nothing. */}
                        {onPay && i.outstanding > 0 ? (
                           <PayBtn type="button" onClick={() => onPay(i)}>
                              Pay now
                           </PayBtn>
                        ) : null}
                     </Right>
                  </Row>
               ))}
            </List>
         )}

         <Actions>
            <Ghost type="button" onClick={onCancel}>
               Close
            </Ghost>
         </Actions>
      </Wrapper>
   );
};

const PayBtn = styled.button`
   margin-top: 6px;
   font-family: inherit;
   font-size: 12px;
   font-weight: 800;
   padding: 6px 12px;
   border: none;
   border-radius: 8px;
   background: ${colors.GREEN};
   color: #fff;
   cursor: pointer;
`;

export default IspAccountInvoices;

const Wrapper = styled.div`
   width: 560px;
   max-width: 100%;
   display: flex;
   flex-direction: column;
   gap: 12px;
`;
const Head = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 12px;
   flex-wrap: wrap;
`;
const Who = styled.div`
   font-size: 15px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;
const Sub = styled.div`
   font-size: 12px;
   color: ${colors.GRAY5};
`;
const Owed = styled.div<{ $on: boolean }>`
   font-size: 14px;
   font-weight: 700;
   color: ${({ $on }) => ($on ? colors.DANGER : colors.SUCCESS)};
`;
const List = styled.div`
   border: 1px solid ${colors.GRAY12};
   border-radius: 10px;
   max-height: 340px;
   overflow-y: auto;
`;
const Row = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 12px;
   padding: 10px 14px;
   border-bottom: 1px solid ${colors.GRAY13};
   &:last-child {
      border-bottom: none;
   }
`;
const Left = styled.div`
   min-width: 0;
`;
const Right = styled.div`
   text-align: right;
   white-space: nowrap;
`;
const Num = styled.div`
   font-family: monospace;
   font-size: 12px;
   color: ${colors.MAIN_DARK};
`;
const Meta = styled.div`
   font-size: 11.5px;
   color: ${colors.GRAY5};
`;
const Late = styled.span`
   margin-left: 8px;
   padding: 1px 7px;
   border-radius: 8px;
   font-size: 10px;
   font-weight: 700;
   background: ${colors.DANGER}18;
   color: ${colors.DANGER};
`;
const Total = styled.div`
   font-size: 13px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;
const Paid = styled.div<{ $part: boolean }>`
   font-size: 11px;
   color: ${({ $part }) => ($part ? colors.ORANGE : colors.GRAY5)};
`;
const Empty = styled.div`
   padding: 20px;
   text-align: center;
   font-size: 12.5px;
   color: ${colors.GRAY5};
   border: 1px solid ${colors.GRAY12};
   border-radius: 10px;
`;
const Actions = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 8px;
`;
const Ghost = styled.button`
   padding: 8px 16px;
   border-radius: 8px;
   border: 1px solid ${colors.GRAY12};
   background: transparent;
   color: inherit;
   font-size: 13px;
   cursor: pointer;
`;
